import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Eye, ChevronDown, ChevronUp, Filter, X } from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { ListPagination } from "@/components/ui/list-pagination";
import { supabase } from "@/integrations/supabase/client";

import {
  getShortlistedFreelancers,
  type ShortlistedFreelancer,
  type ShortlistInfo,
} from "@/services/shortlists-api";
import { DisponibilidadeLinha } from "@/components/freelancers/DisponibilidadeBadge";
import { useDisponibilidadeBatch } from "@/hooks/useDisponibilidade";
import { usePageParam } from "@/hooks/use-page-param";

// Áreas de atuação mais comuns
const AREAS_OPTIONS = [
  "Direção de Arte",
  "Redação",
  "Motion Design",
  "Produção",
  "Planejamento",
  "Mídia",
  "Social Media",
  "Atendimento",
  "Design",
  "Ilustração",
];

interface AreasBadgesProps {
  areas: string[] | null;
}

function AreasBadges({ areas }: AreasBadgesProps) {
  if (!areas || areas.length === 0)
    return <span className="text-muted-foreground">-</span>;

  const maxVisible = 2;
  const visibleAreas = areas.slice(0, maxVisible);
  const hiddenAreas = areas.slice(maxVisible);

  return (
    <div className="flex flex-wrap gap-1">
      {visibleAreas.map((area, index) => (
        <Badge key={index} variant="secondary" className="text-xs">
          {area}
        </Badge>
      ))}
      {hiddenAreas.length > 0 && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger>
              <Badge variant="outline" className="text-xs cursor-help">
                +{hiddenAreas.length}
              </Badge>
            </TooltipTrigger>
            <TooltipContent>
              <div className="flex flex-wrap gap-1 max-w-[200px]">
                {hiddenAreas.map((area, index) => (
                  <Badge key={index} variant="secondary" className="text-xs">
                    {area}
                  </Badge>
                ))}
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  );
}

interface ShortlistsBadgesProps {
  shortlists: ShortlistInfo[];
}

function ShortlistsBadges({ shortlists }: ShortlistsBadgesProps) {
  if (!shortlists || shortlists.length === 0)
    return <span className="text-muted-foreground">-</span>;

  const maxVisible = 2;
  const visibleShortlists = shortlists.slice(0, maxVisible);
  const hiddenShortlists = shortlists.slice(maxVisible);

  return (
    <div className="flex flex-wrap gap-1">
      {visibleShortlists.map((shortlist, index) => (
        <Badge key={index} variant="default" className="text-xs">
          {shortlist.agencia || shortlist.cliente || "Vaga"}
        </Badge>
      ))}
      {hiddenShortlists.length > 0 && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger>
              <Badge variant="outline" className="text-xs cursor-help">
                +{hiddenShortlists.length}
              </Badge>
            </TooltipTrigger>
            <TooltipContent>
              <div className="flex flex-col gap-1 max-w-[250px]">
                {hiddenShortlists.map((shortlist, index) => (
                  <div key={index} className="text-xs">
                    <span className="font-medium">
                      {shortlist.agencia || "Sem agência"}
                    </span>
                    {shortlist.cliente && (
                      <span className="text-muted-foreground">
                        {" "}
                        - {shortlist.cliente}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  );
}

export function ShortlistedFreelancersTable() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);

  // Extrair parâmetros da URL
  const [page] = usePageParam("shortlist_page");
  const areaFilter = searchParams.get("shortlist_area") || "";
  const pretensaoFilter = searchParams.get("shortlist_pretensao") || "";
  const agenciaFilter = searchParams.get("shortlist_agencia") || "";

  const perPage = 10;

  // Query para buscar freelancers em shortlists
  const { data, isLoading, error } = useQuery({
    queryKey: [
      "shortlistedFreelancers",
      page,
      areaFilter,
      pretensaoFilter,
      agenciaFilter,
    ],
    queryFn: () =>
      getShortlistedFreelancers({
        page,
        per_page: perPage,
        area: areaFilter || undefined,
        pretensao: pretensaoFilter || undefined,
        agencia: agenciaFilter || undefined,
        order_by: "ultimo_shortlist",
        order_dir: "desc",
      }),
  });

  // Query para buscar opções de pretensão salarial (valores únicos)
  const { data: pretensaoOptions = [] } = useQuery({
    queryKey: ["shortlistedPretensaoOptions"],
    queryFn: async () => {
      // Buscar valores distintos de pretensao_salarial dos freelancers em shortlist
      const { data } = await supabase
        .from("v_shortlisted_freelancers")
        .select("pretensao_salarial");

      const unique = new Set<string>();
      data?.forEach((item) => {
        if (item.pretensao_salarial) {
          unique.add(item.pretensao_salarial);
        }
      });
      return Array.from(unique).sort();
    },
    staleTime: 5 * 60 * 1000,
  });

  // Query para buscar opções de agência (valores únicos das shortlists)
  const { data: agenciaOptions = [] } = useQuery({
    queryKey: ["shortlistedAgenciaOptions"],
    queryFn: async () => {
      // Buscar shortlists e extrair agências únicas
      const { data } = await supabase
        .from("v_shortlisted_freelancers")
        .select("shortlists");

      const unique = new Set<string>();
      data?.forEach((item) => {
        const shortlists = item.shortlists as Array<{ agencia?: string }> | null;
        shortlists?.forEach((s) => {
          if (s.agencia) {
            unique.add(s.agencia);
          }
        });
      });
      return Array.from(unique).sort();
    },
    staleTime: 5 * 60 * 1000,
  });

  const freelancers = data?.data || [];
  const pagination = data?.pagination;
  const hasActiveFilters = areaFilter || pretensaoFilter || agenciaFilter;

  // Disponibilidade unificada (v_availability_summary) em lote — a mesma verdade
  // da listagem/detalhe/assistente. Ausência de sinal = "Sem info".
  const { data: dispMap } = useDisponibilidadeBatch(
    freelancers.map((freelancer: ShortlistedFreelancer) => freelancer.freelancer_id)
  );

  // Atualizar parâmetros da URL
  const updateSearchParams = (key: string, value: string) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
    // Reset page when filters change
    if (key !== "shortlist_page") {
      newParams.set("shortlist_page", "1");
    }
    setSearchParams(newParams);
  };

  // Limpar todos os filtros
  const clearFilters = () => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete("shortlist_area");
    newParams.delete("shortlist_pretensao");
    newParams.delete("shortlist_agencia");
    newParams.set("shortlist_page", "1");
    setSearchParams(newParams);
  };

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Freelancers em Shortlists</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-destructive">
            Erro ao carregar freelancers em shortlists.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Freelancers em Shortlists</CardTitle>
          <div className="flex items-center gap-2">
            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="h-8 gap-1"
              >
                <X className="h-4 w-4" />
                Limpar filtros
              </Button>
            )}
            <Collapsible open={isFiltersOpen} onOpenChange={setIsFiltersOpen}>
              <CollapsibleTrigger asChild>
                <Button variant="outline" size="sm" className="h-8 gap-1">
                  <Filter className="h-4 w-4" />
                  Filtros
                  {isFiltersOpen ? (
                    <ChevronUp className="h-4 w-4" />
                  ) : (
                    <ChevronDown className="h-4 w-4" />
                  )}
                </Button>
              </CollapsibleTrigger>
            </Collapsible>
          </div>
        </div>

        <Collapsible open={isFiltersOpen} onOpenChange={setIsFiltersOpen}>
          <CollapsibleContent className="pt-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Área</label>
                <Select
                  value={areaFilter || "__all__"}
                  onValueChange={(value) =>
                    updateSearchParams("shortlist_area", value === "__all__" ? "" : value)
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Todas as áreas" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">Todas as áreas</SelectItem>
                    {AREAS_OPTIONS.map((area) => (
                      <SelectItem key={area} value={area}>
                        {area}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Pretensão Salarial</label>
                <Select
                  value={pretensaoFilter || "__all__"}
                  onValueChange={(value) =>
                    updateSearchParams("shortlist_pretensao", value === "__all__" ? "" : value)
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Todas as faixas" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">Todas as faixas</SelectItem>
                    {pretensaoOptions.map((pretensao) => (
                      <SelectItem key={pretensao} value={pretensao}>
                        {pretensao}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Agência</label>
                <Select
                  value={agenciaFilter || "__all__"}
                  onValueChange={(value) =>
                    updateSearchParams("shortlist_agencia", value === "__all__" ? "" : value)
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Todas as agências" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">Todas as agências</SelectItem>
                    {agenciaOptions.map((agencia) => (
                      <SelectItem key={agencia} value={agencia}>
                        {agencia}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CollapsibleContent>
        </Collapsible>
      </CardHeader>

      <CardContent>
        {isLoading ? (
          <div className="flex items-center justify-center h-32">
            <div className="text-muted-foreground">Carregando...</div>
          </div>
        ) : freelancers.length === 0 ? (
          <div className="flex items-center justify-center h-32">
            <div className="text-muted-foreground">
              Nenhum freelancer em shortlist encontrado.
            </div>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome</TableHead>
                    <TableHead>Disponibilidade</TableHead>
                    <TableHead>Área</TableHead>
                    <TableHead>Pretensão Salarial</TableHead>
                    <TableHead>Shortlists</TableHead>
                    <TableHead>Último Shortlist</TableHead>
                    <TableHead className="w-[100px]">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {freelancers.map((freelancer: ShortlistedFreelancer) => (
                    <TableRow key={freelancer.freelancer_id}>
                      <TableCell className="font-medium">
                        {freelancer.nome}
                      </TableCell>
                      <TableCell>
                        <DisponibilidadeLinha
                          summary={dispMap?.get(freelancer.freelancer_id)}
                          level="inline"
                        />
                      </TableCell>
                      <TableCell>
                        <AreasBadges areas={freelancer.areas} />
                      </TableCell>
                      <TableCell>
                        {freelancer.pretensao_salarial || "-"}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <ShortlistsBadges shortlists={freelancer.shortlists} />
                          <Badge variant="outline" className="text-xs">
                            {freelancer.total_shortlists}
                          </Badge>
                        </div>
                      </TableCell>
                      <TableCell>
                        {freelancer.ultimo_shortlist
                          ? format(
                              new Date(freelancer.ultimo_shortlist),
                              "dd/MM/yyyy",
                              { locale: ptBR }
                            )
                          : "-"}
                      </TableCell>
                      <TableCell>
                        <Button asChild variant="ghost" size="icon">
                          <Link
                            to={`/freelancers/${freelancer.freelancer_id}`}
                            title="Ver detalhes"
                          >
                            <Eye className="h-4 w-4" />
                          </Link>
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {pagination && pagination.total_pages > 1 && (
              <div className="flex flex-col sm:flex-row items-center justify-between gap-2 mt-4">
                <p className="text-sm text-muted-foreground">
                  Mostrando {(page - 1) * perPage + 1} a{" "}
                  {Math.min(page * perPage, pagination.total)} de{" "}
                  {pagination.total} freelancers
                </p>
                <ListPagination
                  currentPage={page}
                  totalPages={pagination.total_pages}
                  onPageChange={(pagina) =>
                    updateSearchParams("shortlist_page", pagina.toString())
                  }
                />
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
